import { Injectable, OnDestroy, inject } from '@angular/core';
import { Observable, Subject, fromEvent, throwError } from 'rxjs';
import { catchError, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Socket, io } from 'socket.io-client';
import { ConfigService } from '../../config';
import { injectAuthService } from '../auth/provide-auth-service';

// ---- Message interface definitions ----
/** Sent client -> server to join an entry room */
export interface SubscribeToEntryMessage {
  name: 'entries/subscribe-to-entry';
  body: { entityId: string; entryId: string };
}
/** Sent client -> server to leave an entry room */
export interface UnsubscribeToEntryMessage {
  name: 'entries/unsubscribe-to-entry';
  body: { entityId: string; entryId: string };
}
/** Sent server -> client when workflow updated for an entry */
export interface DataEntryWorkflowUpdatedMessage {
  name: 'entries/workflow-updated';
  body: { entityId: string; entryId: string; eventName: string };
}
/** Sent server -> client when an entry's values are updated */
export interface DataEntryValueUpdatedMessage {
  name: 'entries/value-updated';
  body: { entityId: string; entryId: string };
}
/** Sent server -> client when an entry is archived (single or bulk) */
export interface DataEntryArchivedMessage {
  name: 'entries/archived';
  body: { entityId: string; entryId: string };
}

@Injectable({
  providedIn: 'root',
})
export class SocketService implements OnDestroy {
  private socket: Socket | null = null;
  private destroy$ = new Subject<void>();
  private authService = injectAuthService();
  private configService = inject(ConfigService);

  private readonly WORKFLOW_UPDATED_EVENT = 'entries/workflow-updated';
  private readonly VALUE_UPDATED_EVENT = 'entries/value-updated';
  private readonly ENTRY_ARCHIVED_EVENT = 'entries/archived';

  /**
   * Connect to the socket.io server with authentication
   */
  connect(): Observable<any> {
    if (this.socket?.connected) {
      return new Observable((observer) => {
        observer.next(true);
        observer.complete();
      });
    }

    return this.authService.getAccessToken().pipe(
      tap((token) => {
        const socketUrl = this.configService.configuration?.SOCKET_BASE_URL;
        this.socket = io(socketUrl, {
          transports: ['websocket', 'polling'],
          auth: {
            token: `${token}`,
          },
          autoConnect: true,
        });
      }),
      switchMap(() => {
        // Wait for connection to be established
        return new Observable((observer) => {
          if (this.socket?.connected) {
            observer.next(true);
            observer.complete();
            return () => {}; // Return empty teardown function
          }

          const onConnect = () => {
            observer.next(true);
            observer.complete();
          };

          const onError = (error: any) => {
            observer.error(error);
          };

          this.socket?.on('connect', onConnect);
          this.socket?.on('connect_error', onError);

          return () => {
            this.socket?.off('connect', onConnect);
            this.socket?.off('connect_error', onError);
          };
        });
      }),
      catchError((error) => {
        return throwError(() => error);
      }),
    );
  }

  /**
   * Disconnect from the socket.io server
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }
  /**
   * Subscribe to notification events
   */
  subscribeToNotifications(): Observable<any> {
    if (!this.socket) {
      return throwError(() => new Error('Socket not connected'));
    }

    // Emit subscription request to the server
    this.socket.emit('subscribeToNotifications');

    // Listen for subscription confirmation
    const subscribed$ = fromEvent(
      this.socket,
      'subscribedToNotifications',
    ).pipe(takeUntil(this.destroy$));

    // Listen for incoming notifications
    const notifications$ = fromEvent(this.socket, 'notification').pipe(
      takeUntil(this.destroy$),
    );

    // Return observable that emits both subscription confirmation and notifications
    return new Observable((observer) => {
      const subSubscription = subscribed$.subscribe(() => {
        observer.next({ type: 'subscribed' });
      });

      const notifSubscription = notifications$.subscribe((notification) => {
        observer.next({ type: 'notification', data: notification });
      });

      return () => {
        subSubscription.unsubscribe();
        notifSubscription.unsubscribe();
      };
    });
  }

  subscribeToModifiedRequestsCounts(): Observable<any> {
    if (!this.socket) {
      return throwError(() => new Error('Socket not connected'));
    }

    // Listen for incoming notifications
    const requestEntryModified$ = fromEvent(
      this.socket,
      'request-entry-modified',
    ).pipe(takeUntil(this.destroy$));

    // Return observable that emits both subscription confirmation and notifications
    return new Observable((observer) => {
      const notifSubscription = requestEntryModified$.subscribe(
        (notification) => {
          observer.next({ type: 'request-entry-modified', data: notification });
        },
      );

      return () => {
        notifSubscription.unsubscribe();
      };
    });
  }
  /**
   * Subscribe to an entry room; emits a 'subscribed' ack (if server sends one)
   * and subsequent workflow updated events.
   */
  subscribeToEntry(
    entityId: string,
    entryId: string,
  ): Observable<{
    type: 'subscribed' | 'workflow-updated' | 'value-updated' | 'archived';
    data: any;
  }> {
    if (!this.socket) {
      return throwError(() => new Error('Socket not connected'));
    }

    return new Observable<{
      type: 'subscribed' | 'workflow-updated' | 'value-updated' | 'archived';
      data: any;
    }>((observer) => {
      let active = true;
      const subscribeMsg: SubscribeToEntryMessage = {
        name: 'entries/subscribe-to-entry',
        body: { entityId, entryId },
      };

      const ackCb = (ack: any) => {
        if (!active) return;
        observer.next({ type: 'subscribed', data: ack });
      };
      this.socket!.emit(subscribeMsg.name, subscribeMsg.body, ackCb);

      const matchesEntry = (payload: { entityId: string; entryId: string }) =>
        payload?.entityId === entityId && payload?.entryId === entryId;

      const workflowHandler = (
        payload: DataEntryWorkflowUpdatedMessage['body'],
      ) => {
        if (!active) return;
        if (matchesEntry(payload)) {
          observer.next({ type: 'workflow-updated', data: payload });
        }
      };

      const valueUpdatedHandler = (
        payload: DataEntryValueUpdatedMessage['body'],
      ) => {
        if (!active) return;
        if (matchesEntry(payload)) {
          observer.next({ type: 'value-updated', data: payload });
        }
      };

      const archivedHandler = (payload: DataEntryArchivedMessage['body']) => {
        if (!active) return;
        if (matchesEntry(payload)) {
          observer.next({ type: 'archived', data: payload });
        }
      };

      this.socket!.on(this.WORKFLOW_UPDATED_EVENT, workflowHandler);
      this.socket!.on(this.VALUE_UPDATED_EVENT, valueUpdatedHandler);
      this.socket!.on(this.ENTRY_ARCHIVED_EVENT, archivedHandler);

      return () => {
        active = false;
        this.socket!.off(this.WORKFLOW_UPDATED_EVENT, workflowHandler);
        this.socket!.off(this.VALUE_UPDATED_EVENT, valueUpdatedHandler);
        this.socket!.off(this.ENTRY_ARCHIVED_EVENT, archivedHandler);
        this.unsubscribeFromEntry(entityId, entryId);
      };
    }).pipe(takeUntil(this.destroy$));
  }

  /** Convenience observable that only streams workflow updates for an entry (assumes already subscribed elsewhere) */
  onEntryWorkflowUpdated(
    entityId: string,
    entryId: string,
  ): Observable<DataEntryWorkflowUpdatedMessage['body']> {
    if (!this.socket) {
      return throwError(() => new Error('Socket not connected'));
    }
    return new Observable<DataEntryWorkflowUpdatedMessage['body']>(
      (observer) => {
        const handler = (payload: DataEntryWorkflowUpdatedMessage['body']) => {
          if (payload?.entityId === entityId && payload?.entryId === entryId) {
            observer.next(payload);
          }
        };
        this.socket!.on(this.WORKFLOW_UPDATED_EVENT, handler);
        return () => this.socket!.off(this.WORKFLOW_UPDATED_EVENT, handler);
      },
    ).pipe(takeUntil(this.destroy$));
  }

  /** Emit unsubscribe message (does not remove listeners itself) */
  unsubscribeFromEntry(entityId: string, entryId: string): void {
    if (!this.socket) return;
    const msg: UnsubscribeToEntryMessage = {
      name: 'entries/unsubscribe-to-entry',
      body: { entityId, entryId },
    };
    this.socket.emit(msg.name, msg.body);
  }

  /**
   * Check if socket is connected
   */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Get the current socket instance
   */
  getSocket(): Socket | null {
    return this.socket;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.disconnect();
  }
}
